import React from 'react';
import { Box, CircularProgress, Typography, Skeleton } from '@mui/material';
import { styled } from '@mui/material/styles';

const LoadingContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  minHeight: '200px',
  padding: theme.spacing(3),
}));

const SkeletonContainer = styled(Box)(({ theme }) => ({
  width: '100%',
  maxWidth: '800px',
  padding: theme.spacing(2),
}));

// Simple loading spinner for critical paths
export const SimpleLoader = ({ message = "Loading..." }) => (
  <LoadingContainer>
    <CircularProgress size={40} />
    <Typography variant="body2" sx={{ mt: 2, color: 'text.secondary' }}>
      {message}
    </Typography>
  </LoadingContainer>
);

// Skeleton loader for page content
export const PageSkeleton = () => (
  <SkeletonContainer>
    {/* Header skeleton */}
    <Skeleton variant="rectangular" height={60} sx={{ mb: 2 }} />
    
    {/* Content skeletons */}
    <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
      <Skeleton variant="rectangular" width="30%" height={200} />
      <Box sx={{ flex: 1 }}>
        <Skeleton variant="text" height={40} sx={{ mb: 1 }} />
        <Skeleton variant="text" height={20} sx={{ mb: 1 }} />
        <Skeleton variant="text" height={20} width="60%" />
      </Box>
    </Box>
    
    {/* Grid skeleton */}
    <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))', gap: 2 }}>
      {[...Array(6)].map((_, index) => (
        <Box key={index}>
          <Skeleton variant="rectangular" height={150} sx={{ mb: 1 }} />
          <Skeleton variant="text" height={20} sx={{ mb: 0.5 }} />
          <Skeleton variant="text" height={16} width="60%" />
        </Box>
      ))}
    </Box>
  </SkeletonContainer>
);

// Product list skeleton
export const ProductListSkeleton = () => (
  <SkeletonContainer>
    <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 2 }}>
      {[...Array(8)].map((_, index) => (
        <Box key={index}>
          <Skeleton variant="rectangular" height={200} sx={{ mb: 1 }} />
          <Skeleton variant="text" height={20} sx={{ mb: 0.5 }} />
          <Skeleton variant="text" height={16} width="80%" sx={{ mb: 0.5 }} />
          <Skeleton variant="text" height={24} width="40%" />
        </Box>
      ))}
    </Box>
  </SkeletonContainer>
);

// Shop list skeleton
export const ShopListSkeleton = () => (
  <SkeletonContainer>
    {[...Array(5)].map((_, index) => (
      <Box key={index} sx={{ display: 'flex', gap: 2, mb: 3, p: 2, border: '1px solid #e0e0e0', borderRadius: 1 }}>
        <Skeleton variant="circular" width={80} height={80} />
        <Box sx={{ flex: 1 }}>
          <Skeleton variant="text" height={24} width="60%" sx={{ mb: 1 }} />
          <Skeleton variant="text" height={16} sx={{ mb: 1 }} />
          <Skeleton variant="text" height={16} width="40%" sx={{ mb: 1 }} />
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Skeleton variant="rectangular" width={60} height={20} />
            <Skeleton variant="rectangular" width={80} height={20} />
          </Box>
        </Box>
      </Box>
    ))}
  </SkeletonContainer>
);

// Order list skeleton
export const OrderListSkeleton = () => (
  <SkeletonContainer>
    {[...Array(4)].map((_, index) => (
      <Box key={index} sx={{ mb: 2, p: 2, border: '1px solid #e0e0e0', borderRadius: 1 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
          <Skeleton variant="text" width="30%" height={20} />
          <Skeleton variant="rectangular" width={80} height={24} />
        </Box>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Skeleton variant="rectangular" width={60} height={60} />
          <Box sx={{ flex: 1 }}>
            <Skeleton variant="text" height={18} sx={{ mb: 0.5 }} />
            <Skeleton variant="text" height={16} width="60%" sx={{ mb: 0.5 }} />
            <Skeleton variant="text" height={20} width="40%" />
          </Box>
        </Box>
      </Box>
    ))}
  </SkeletonContainer>
);

// Enhanced loading fallback with route-specific skeletons
export const LoadingFallback = ({ type = 'simple', message }) => {
  switch (type) {
    case 'page':
      return <PageSkeleton />;
    case 'products':
      return <ProductListSkeleton />;
    case 'shops':
      return <ShopListSkeleton />;
    case 'orders':
      return <OrderListSkeleton />;
    default:
      return <SimpleLoader message={message} />;
  }
};

export default LoadingFallback;

